import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Database, Save, Check, Trash2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const DATA_OPTIONS = [
  { value: '30', label: '30 days' },
  { value: '60', label: '60 days' },
  { value: '90', label: '90 days' },
  { value: '180', label: '180 days' },
  { value: '365', label: '1 year' },
  { value: 'forever', label: 'Forever (no auto-delete)' },
];

const LOG_OPTIONS = [
  { value: '30', label: '30 days' },
  { value: '60', label: '60 days' },
  { value: '90', label: '90 days' },
  { value: '180', label: '180 days' },
  { value: '365', label: '1 year' },
];

export default function DataRetentionPanel() {
  const [dataRetention, setDataRetention] = useState('90');
  const [logRetention, setLogRetention] = useState('90');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const { data } = await axios.get(`${API}/api/settings/retention`, { withCredentials: true });
      setDataRetention(data.data_retention_days === null ? 'forever' : String(data.data_retention_days || 90));
      setLogRetention(String(data.log_retention_days || 90));
    } catch { /* ignore */ }
  };

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    try {
      await axios.post(`${API}/api/settings/retention`, {
        data_retention_days: dataRetention === 'forever' ? null : parseInt(dataRetention),
        log_retention_days: parseInt(logRetention),
      }, { withCredentials: true });
      setSaved(true);
      toast.success('Retention settings updated.');
      setTimeout(() => setSaved(false), 3000);
    } catch {
      toast.error('Failed to save retention settings.');
    }
    finally { setSaving(false); }
  };

  const handleDeleteAll = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/api/settings/delete-all-data`, { withCredentials: true });
      setShowDeleteConfirm(false);
      toast.success('All data has been deleted.');
    } catch {
      toast.error('Failed to delete data.');
    }
    finally { setDeleting(false); }
  };

  const retentionText = dataRetention === 'forever'
    ? 'Your data is kept indefinitely. No automatic deletion.'
    : `Data older than ${dataRetention} days will be automatically deleted.`;

  return (
    <div className="border border-[#27272A] rounded-lg bg-[#1A1A1A] p-6 transition-all duration-200 hover:border-[#7B2FFF]/30">
      <div className="flex items-center gap-2 mb-5">
        <Database className="w-4 h-4 text-[#7B2FFF]" />
        <h3 className="text-sm font-semibold text-[#EDEDF0]" style={{ fontFamily: "'Unbounded', sans-serif" }}>
          Data Retention
        </h3>
      </div>

      <div className="space-y-5">
        {/* Conversation / webhook data retention */}
        <div className="space-y-1.5">
          <Label className="text-xs text-[#A1A1AA]">Conversation / webhook data retention</Label>
          <Select value={dataRetention} onValueChange={setDataRetention}>
            <SelectTrigger
              data-testid="data-retention-select"
              className="bg-[#111111] border-[#27272A] text-[#EDEDF0] focus:border-[#7B2FFF] focus:ring-[#7B2FFF] rounded-md h-10"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#1A1A1A] border-[#27272A] text-[#EDEDF0]">
              {DATA_OPTIONS.map(({ value, label }) => (
                <SelectItem key={value} value={value} className="focus:bg-[#7B2FFF]/10 focus:text-[#EDEDF0]">
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Activity log retention */}
        <div className="space-y-1.5">
          <Label className="text-xs text-[#A1A1AA]">Activity log retention</Label>
          <Select value={logRetention} onValueChange={setLogRetention}>
            <SelectTrigger
              data-testid="log-retention-select"
              className="bg-[#111111] border-[#27272A] text-[#EDEDF0] focus:border-[#7B2FFF] focus:ring-[#7B2FFF] rounded-md h-10"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#1A1A1A] border-[#27272A] text-[#EDEDF0]">
              {LOG_OPTIONS.map(({ value, label }) => (
                <SelectItem key={value} value={value} className="focus:bg-[#7B2FFF]/10 focus:text-[#EDEDF0]">
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Helper text */}
        <p className="text-xs text-[#71717A] italic" data-testid="retention-helper-text">
          {retentionText}
        </p>

        {/* Save button */}
        <Button
          data-testid="save-retention-button"
          onClick={handleSave}
          disabled={saving}
          className="bg-[#7B2FFF] hover:bg-[#9353FF] text-white font-medium rounded-md h-9 text-sm transition-all duration-200 hover:shadow-[0_0_16px_rgba(123,47,255,0.25)] active:scale-[0.98] disabled:opacity-40"
        >
          {saving ? (
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : saved ? (
            <span className="flex items-center gap-1.5"><Check className="w-4 h-4" /> Saved</span>
          ) : (
            <span className="flex items-center gap-1.5"><Save className="w-4 h-4" /> Save Retention Settings</span>
          )}
        </Button>

        {/* Separator */}
        <div className="border-t border-[#27272A] pt-5 mt-5">
          <div className="flex items-start gap-3 p-3 mb-4 rounded-md bg-red-500/5 border border-red-500/10">
            <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
            <p className="text-xs text-[#A1A1AA] leading-relaxed">
              This action permanently deletes all your webhook data, conversation history, and activity logs.
              This cannot be undone.
            </p>
          </div>

          {!showDeleteConfirm ? (
            <Button
              data-testid="delete-all-data-button"
              onClick={() => setShowDeleteConfirm(true)}
              variant="outline"
              className="border-red-500/30 bg-transparent text-red-400 hover:bg-red-500/10 hover:border-red-500/50 rounded-md h-9 text-sm"
            >
              <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Delete All My Data
            </Button>
          ) : (
            <div className="p-4 rounded-md bg-[#111111] border border-red-500/20 space-y-3">
              <p className="text-sm text-[#EDEDF0]">
                This will permanently delete all your data. This cannot be undone.
              </p>
              <div className="flex gap-2">
                <Button
                  data-testid="confirm-delete-all-data"
                  onClick={handleDeleteAll}
                  disabled={deleting}
                  className="bg-red-500 hover:bg-red-600 text-white h-9 text-sm px-4"
                >
                  {deleting ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : 'Yes, delete everything'}
                </Button>
                <Button
                  data-testid="cancel-delete-all-data"
                  onClick={() => setShowDeleteConfirm(false)}
                  variant="ghost"
                  className="text-[#A1A1AA] hover:text-[#EDEDF0] h-9 text-sm"
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
